/**
 * TypeScript types for Negotiation Playbook
 */

export type EntryType =
  | 'opening_script'
  | 'objection_response'
  | 'seller_finance_pitch'
  | 'closing_technique'
  | 'power_phrase'
  | 'transition_phrase'
  | 'closing_phrase'
  | 'lesson'
  | 'motivation_signal';

export type DealContext =
  | 'seller_finance'
  | 'subject_to'
  | 'wholesale'
  | 'cash_offer'
  | 'creative_finance'
  | 'any';

export type SellerSituation =
  | 'foreclosure'
  | 'low_equity'
  | 'vacant'
  | 'days_on_market'
  | 'motivated'
  | 'price_reduced'
  | 'fsbo'
  | 'expired_listing'
  | 'divorce'
  | 'inheritance'
  | 'any';

export interface PlaybookEntry {
  id: string;
  content: string;
  entry_type: EntryType;
  content_type?: string;
  video_id?: string;
  objection_text?: string;
  deal_contexts: DealContext[];
  seller_situations: SellerSituation[];
  effectiveness_score?: number;
  usage_count: number;
  tags: string[];
  created_at?: string;
}

export interface PlaybookSuggestion {
  id: string;
  content: string;
  type: EntryType;
  objection?: string;
  score?: number;
}

export interface InferredMotivation {
  signal: SellerSituation;
  confidence: number;
  source: string;
  value?: string;
}

export interface PlaybookSearchResponse {
  entries: PlaybookEntry[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface PlaybookSuggestionsResponse {
  opening_scripts: PlaybookSuggestion[];
  objection_responses: PlaybookSuggestion[];
  seller_finance_pitches: PlaybookSuggestion[];
  closing_techniques: PlaybookSuggestion[];
  power_phrases: PlaybookSuggestion[];
  total_suggestions: number;
  // Inferred motivations from property data
  inferred_motivations?: InferredMotivation[];
  motivation_level?: 'high' | 'medium' | 'low';
}

export interface ObjectionResponseGroup {
  objection: string;
  responses: PlaybookSuggestion[];
}

export interface ObjectionMatrixResponse {
  objections: ObjectionResponseGroup[];
  total_objections: number;
}

export interface ContextSuggestionsRequest {
  property_id?: number;  // If provided, auto-infers motivations from DB
  financing_type?: string;
  deal_type?: string;
  days_on_market?: number;
  price_reduction_count?: number;
  is_foreclosure?: boolean;
  is_vacant?: boolean;
  has_low_equity?: boolean;
  limit_per_type?: number;
}

export interface PlaybookStats {
  total_entries: number;
  by_type: Record<string, number>;
  by_deal_context: Record<string, number>;
  by_seller_situation: Record<string, number>;
  top_used_entries: PlaybookSuggestion[];
}

// Display names for entry types
export const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  opening_script: 'Opening Scripts',
  objection_response: 'Objection Responses',
  seller_finance_pitch: 'Seller Finance Pitches',
  closing_technique: 'Closing Techniques',
  power_phrase: 'Power Phrases',
  transition_phrase: 'Transitions',
  closing_phrase: 'Closing Phrases',
  lesson: 'Lessons',
  motivation_signal: 'Motivation Signals',
};

// Icons for entry types (Lucide icon names)
export const ENTRY_TYPE_ICONS: Record<EntryType, string> = {
  opening_script: 'Phone',
  objection_response: 'Shield',
  seller_finance_pitch: 'DollarSign',
  closing_technique: 'Target',
  power_phrase: 'Zap',
  transition_phrase: 'ArrowRight',
  closing_phrase: 'CheckCircle',
  lesson: 'Lightbulb',
  motivation_signal: 'AlertCircle',
};

// Display labels for motivation signals
export const MOTIVATION_SIGNAL_LABELS: Record<SellerSituation, string> = {
  foreclosure: 'Foreclosure',
  low_equity: 'Low Equity',
  vacant: 'Vacant',
  days_on_market: 'Days on Market',
  motivated: 'Motivated',
  price_reduced: 'Price Reduced',
  fsbo: 'FSBO',
  expired_listing: 'Expired Listing',
  divorce: 'Divorce',
  inheritance: 'Inheritance',
  any: 'Any Situation',
};
